import { useEffect, useMemo, useState } from 'react';
import { HandCoins } from 'lucide-react';
import { operationsApi, type AdvanceDto } from '@/features/operations/api';
import { fieldUserApi } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';
import { PageHeader } from '@/lib/ui';

type Tab = 'pending' | 'byUser';

type FieldUser = {
  id: string;
  employee_id: string;
  full_name: string;
  status: string;
};

const statusClass: Record<AdvanceDto['status'], string> = {
  REQUESTED: 'bg-amber-500/15 text-amber-300',
  APPROVED: 'bg-emerald-500/15 text-emerald-300',
  DISBURSED: 'bg-cyan-500/15 text-cyan-300',
  REJECTED: 'bg-rose-500/15 text-rose-300',
  DEDUCTED: 'bg-slate-500/15 text-slate-300',
};

function money(minor: number, currency = 'AED') {
  return `${currency} ${(minor / 100).toFixed(2)}`;
}

export function AdvancesPage() {
  const canApprove = useMemo(() => {
    const perms = useAuthStore.getState().tenant?.permissions ?? [];
    return perms.includes('advance.approve');
  }, []);

  const [tab, setTab] = useState<Tab>('pending');
  const [users, setUsers] = useState<FieldUser[]>([]);
  const [pending, setPending] = useState<AdvanceDto[]>([]);
  const [userId, setUserId] = useState('');
  const [userRows, setUserRows] = useState<AdvanceDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decideFor, setDecideFor] = useState<{
    advance: AdvanceDto;
    decision: 'APPROVE' | 'REJECT';
  } | null>(null);

  // Field users — drives the name lookup + the "by user" selector.
  useEffect(() => {
    void (async () => {
      try {
        setUsers((await fieldUserApi.list()) as FieldUser[]);
      } catch (e) {
        setError((e as Error).message || 'Failed to load field users');
      }
    })();
  }, []);

  const userMap = useMemo(() => {
    const m: Record<string, FieldUser> = {};
    users.forEach((u) => (m[u.id] = u));
    return m;
  }, [users]);

  const userLabel = (id: string) => {
    const u = userMap[id];
    return u ? `${u.employee_id} · ${u.full_name}` : id;
  };

  const loadPending = async () => {
    setLoading(true);
    setError(null);
    try {
      setPending(await operationsApi.listPendingAdvances());
    } catch (e) {
      setError((e as Error).message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const loadForUser = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      setUserRows(await operationsApi.listAdvancesForUser(userId));
    } catch (e) {
      setError((e as Error).message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'pending') void loadPending();
  }, [tab]);

  useEffect(() => {
    if (tab === 'byUser' && userId) void loadForUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tab]);

  const reloadActive = async () => {
    await loadPending();
    if (tab === 'byUser') await loadForUser();
  };

  const markDisbursed = async (a: AdvanceDto) => {
    try {
      await operationsApi.markDisbursed(a.id);
      await reloadActive();
    } catch (e) {
      setError((e as Error).message || 'Failed to mark disbursed');
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-5">
        <PageHeader
          title="Advances"
          icon={HandCoins}
          subtitle="Salary-advance requests from the mobile app. Approve or reject, then mark disbursed; deducted at the next payroll finalize."
          className="!mb-0"
        />
      </div>

      <div className="flex gap-1 border-b border-border px-4">
        <TabBtn active={tab === 'pending'} onClick={() => setTab('pending')}>
          Pending
          {pending.length > 0 && (
            <span className="ml-2 rounded bg-amber-500/20 px-1 text-xs text-amber-300">
              {pending.length}
            </span>
          )}
        </TabBtn>
        <TabBtn active={tab === 'byUser'} onClick={() => setTab('byUser')}>
          By field user
        </TabBtn>
      </div>

      {error && (
        <div className="border-b border-rose-500/40 bg-rose-500/10 px-6 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-auto p-6">
        {tab === 'pending' && (
          <>
            <div className="mb-3 flex items-center gap-3">
              <p className="text-sm text-muted-foreground">
                {loading
                  ? 'Loading…'
                  : `${pending.length} pending request${pending.length === 1 ? '' : 's'}`}
              </p>
              <button className="btn-secondary" onClick={loadPending} disabled={loading}>
                Refresh
              </button>
            </div>
            <AdvanceTable
              rows={pending}
              userLabel={userLabel}
              canApprove={canApprove}
              onDecide={(advance, decision) => setDecideFor({ advance, decision })}
              onDisburse={markDisbursed}
              emptyLabel="No pending advance requests."
            />
          </>
        )}

        {tab === 'byUser' && (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-xs uppercase text-muted-foreground">Field user</span>
                <select
                  className="input w-[22rem]"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                >
                  <option value="">— select —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.employee_id} · {u.full_name}
                    </option>
                  ))}
                </select>
              </label>
              <button className="btn-secondary" onClick={loadForUser} disabled={loading || !userId}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            <AdvanceTable
              rows={userRows}
              userLabel={userLabel}
              canApprove={canApprove}
              showStatus
              onDecide={(advance, decision) => setDecideFor({ advance, decision })}
              onDisburse={markDisbursed}
              emptyLabel={userId ? 'No advances for this field user.' : 'Select a field user.'}
            />
          </>
        )}
      </div>

      {decideFor && (
        <DecisionModal
          advance={decideFor.advance}
          decision={decideFor.decision}
          userLabel={userLabel(decideFor.advance.field_user_id)}
          onClose={() => setDecideFor(null)}
          onDone={async () => {
            setDecideFor(null);
            await reloadActive();
          }}
        />
      )}
    </div>
  );
}

// ── Shared table ──────────────────────────────────────────────────────────
function AdvanceTable({
  rows,
  userLabel,
  canApprove,
  showStatus,
  onDecide,
  onDisburse,
  emptyLabel,
}: {
  rows: AdvanceDto[];
  userLabel: (id: string) => string;
  canApprove: boolean;
  showStatus?: boolean;
  onDecide: (advance: AdvanceDto, decision: 'APPROVE' | 'REJECT') => void;
  onDisburse: (advance: AdvanceDto) => void;
  emptyLabel: string;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase text-muted-foreground">
        <tr className="border-b border-border">
          <th className="py-2 pr-4">Field user</th>
          <th className="py-2 pr-4">Amount</th>
          <th className="py-2 pr-4">Reason</th>
          <th className="py-2 pr-4">Requested</th>
          {showStatus && <th className="py-2 pr-4">Status</th>}
          <th className="py-2"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a) => (
          <tr key={a.id} className="border-b border-border/60">
            <td className="py-2 pr-4 text-xs">{userLabel(a.field_user_id)}</td>
            <td className="py-2 pr-4 font-mono tabular-nums">
              {money(a.amount_minor, a.currency)}
            </td>
            <td className="py-2 pr-4 text-muted-foreground">{a.reason ?? '—'}</td>
            <td className="py-2 pr-4 text-xs">{new Date(a.requested_at).toLocaleString()}</td>
            {showStatus && (
              <td className="py-2 pr-4">
                <span className={'rounded px-2 py-0.5 text-xs ' + statusClass[a.status]}>
                  {a.status}
                </span>
                {a.status === 'REJECTED' && a.rejection_reason && (
                  <span className="ml-2 text-xs text-muted-foreground">{a.rejection_reason}</span>
                )}
              </td>
            )}
            <td className="space-x-3 py-2 text-right text-xs">
              {a.status === 'REQUESTED' && canApprove && (
                <>
                  <button
                    className="text-emerald-300 hover:underline"
                    onClick={() => onDecide(a, 'APPROVE')}
                  >
                    Approve
                  </button>
                  <button
                    className="text-rose-300 hover:underline"
                    onClick={() => onDecide(a, 'REJECT')}
                  >
                    Reject
                  </button>
                </>
              )}
              {a.status === 'APPROVED' && canApprove && (
                <button className="text-cyan-300 hover:underline" onClick={() => onDisburse(a)}>
                  Mark disbursed
                </button>
              )}
            </td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td
              colSpan={showStatus ? 6 : 5}
              className="py-6 text-center text-sm text-muted-foreground"
            >
              {emptyLabel}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// ── Decision modal (approve / reject) ───────────────────────────────────────
function DecisionModal({
  advance,
  decision,
  userLabel,
  onClose,
  onDone,
}: {
  advance: AdvanceDto;
  decision: 'APPROVE' | 'REJECT';
  userLabel: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (decision === 'REJECT' && !reason.trim()) {
      setErr('A reason is required when rejecting.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await operationsApi.decideAdvance(advance.id, {
        decision,
        rejection_reason: decision === 'REJECT' ? reason.trim() : undefined,
      });
      onDone();
    } catch (e) {
      setErr((e as Error).message || 'Failed');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="card w-full max-w-md space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {decision === 'APPROVE' ? 'Approve advance' : 'Reject advance'}
          </h2>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          {userLabel} · {money(advance.amount_minor, advance.currency)}
          {advance.reason ? ` · ${advance.reason}` : ''}
        </p>
        {decision === 'REJECT' && (
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">Reason (required)</span>
            <input
              className="input w-full"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
          </label>
        )}
        {err && <p className="text-xs text-rose-300">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={decision === 'REJECT' ? 'btn-danger' : 'btn-primary'}
            onClick={save}
            disabled={busy}
          >
            {busy ? 'Saving…' : decision === 'APPROVE' ? 'Approve' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'border-b-2 px-3 py-2 text-sm transition-colors ' +
        (active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground')
      }
    >
      {children}
    </button>
  );
}
